import React, { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Loader2, RefreshCw, Trophy } from "lucide-react";
import { useT } from "@/lib/LanguageContext";
// Relative: a pure module, resolvable under Jest without a shim.
import { QUIZ } from "../../constants/testIds/quiz";

const unanswered = (questions) => questions.map(() => -1);

/**
 * The quiz player shared by the course player (a quiz lesson, public/LearnPlayer.jsx) and
 * the pathway section page (a quiz step, public/pathways/StepRow.jsx). Both are graded by the same
 * server engine (contract §8), so they take the same answers and return the same result.
 *
 * WHAT IT SENDS. `onSubmit({ answers, open_answers })`:
 *   answers:      number[]  one entry per question, in order: the chosen choice index for
 *                           an mcq, -1 for an open question
 *   open_answers: { [questionId]: string }  the text of every open question
 *
 * `onSubmit` resolves with the grading result (`score`, `passed`, `correct_count`,
 * `total`, `open_total`, `detail[]`, and for a pathway `attempts_left`) or with `null`
 * when the caller already told the user about a failure itself. A REJECTION is shown
 * inside the quiz, worded by `describeError(err)`.
 *
 * WHAT IT NEVER KNOWS: the answer key. The feedback on each choice is read from the
 * grading `detail` (question_id -> is_correct), which says whether THE LEARNER'S answer
 * was right, never which answer was.
 *
 * @param {object}   props
 * @param {{ id: string, question: string, question_type?: "mcq"|"open", choices: string[] }[]} props.questions
 * @param {(body: { answers: number[], open_answers: Object<string,string> }) => Promise<object|null>} props.onSubmit
 * @param {object|null} [props.result]        a result to show (e.g. the last attempt); replaced by
 *                                            the next submission, cleared by "retry"
 * @param {number|null} [props.attemptsLeft]  null/undefined = unlimited (nothing shown);
 *                                            0 = no attempt left (submission refused)
 * @param {boolean}  [props.disabled]         freezes every control (e.g. an archived pathway)
 * @param {React.ReactNode | ((shown: object|null) => React.ReactNode)} [props.header]
 *                                            rendered first INSIDE the quiz root (title,
 *                                            threshold); a function receives the result
 *                                            currently shown (null before a submission)
 * @param {string}   [props.resetKey]         a change of it clears the answers and the result
 * @param {(err: any) => string} [props.describeError]  sentence for a rejected submission
 */
export default function QuizView({
  questions,
  onSubmit,
  result = null,
  attemptsLeft = null,
  disabled = false,
  header = null,
  resetKey,
  describeError,
}) {
  const t = useT("admin");
  const tp = useT("public");
  const [answers, setAnswers] = useState(() => unanswered(questions));
  const [openAnswers, setOpenAnswers] = useState({});
  const [shown, setShown] = useState(result);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // A new `result` from the caller (a reloaded step) replaces what is shown.
  useEffect(() => { setShown(result); }, [result]);

  // Reset when the quiz itself changes (intentional: only on identity or question count).
  useEffect(() => {
    setAnswers(unanswered(questions));
    setOpenAnswers({});
    setShown(result);
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, questions.length]);

  const resultByQuestion = useMemo(() => {
    const map = {};
    (shown?.detail || []).forEach((d) => { map[d.question_id] = d; });
    return map;
  }, [shown]);

  // The freshest count wins: a pathway result states the attempts left AFTER this attempt.
  const left = typeof shown?.attempts_left === "number" ? shown.attempts_left
    : (typeof attemptsLeft === "number" ? attemptsLeft : null);
  const exhausted = left === 0;
  const locked = disabled || busy;

  const allAnswered = questions.every((q, i) => {
    if ((q.question_type || "mcq") === "open") return (openAnswers[q.id] || "").trim().length > 0;
    return answers[i] >= 0;
  });

  const handleSubmit = async () => {
    setBusy(true);
    setError("");
    try {
      const next = await onSubmit({ answers, open_answers: openAnswers });
      if (next) setShown(next);
    } catch (e) {
      setError(describeError ? describeError(e) : tp("quizView.submitFailed"));
    } finally {
      setBusy(false);
    }
  };

  const retry = () => {
    setAnswers(unanswered(questions));
    setOpenAnswers({});
    setShown(null);
    setError("");
  };

  const head = typeof header === "function" ? header(shown) : header;

  if (questions.length === 0) {
    return (
      <div data-testid={QUIZ.quiz}>
        {head}
        <div className="text-center text-eh-muted py-12">{tp("learn.quizEmptyState")}</div>
      </div>
    );
  }

  return (
    <div data-testid={QUIZ.quiz}>
      {head}

      {shown && (
        <div
          className={`rounded-xl p-4 mb-6 inline-flex items-center gap-3 ${
            shown.passed ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : "bg-rose-50 border border-rose-200 text-rose-800"
          }`}
          data-testid={QUIZ.quizResult}
          role="status"
        >
          {shown.passed ? <Trophy className="w-5 h-5" /> : <RefreshCw className="w-5 h-5" />}
          <span className="font-medium">
            {shown.passed ? t("learn.quizPassed") : t("learn.quizFailed")} — {shown.score}% ({shown.correct_count}/{shown.total})
            {shown.open_total > 0 && ` · ${tp("learn.openAnswersToReview", { n: shown.open_total })}`}
          </span>
        </div>
      )}

      <div className="space-y-6 mb-8">
        {questions.map((q, qIdx) => {
          const qid = q.id ?? String(qIdx);
          if ((q.question_type || "mcq") === "open") {
            return (
              <div key={qid} className="bg-violet-50/50 rounded-2xl p-5 border border-violet-100" data-testid={`quiz-q-${qIdx}`}>
                <label htmlFor={`quiz-open-input-${qid}`} className="block font-medium mb-1">
                  {qIdx + 1}. {q.question || <em className="text-eh-muted">{tp("learn.emptyQuestion")}</em>}
                </label>
                <div className="text-xs text-violet-700 mb-3">{t("learn.openAnswerHint")}</div>
                <textarea
                  id={`quiz-open-input-${qid}`}
                  value={openAnswers[q.id] || ""}
                  onChange={(e) => setOpenAnswers({ ...openAnswers, [q.id]: e.target.value })}
                  rows={4}
                  maxLength={5000}
                  disabled={!!shown || locked}
                  placeholder={t("learn.openAnswerPlaceholder")}
                  data-testid={QUIZ.quizOpenAnswer(qid)}
                  className="w-full border border-violet-200 rounded-lg p-3 text-sm bg-white disabled:opacity-70"
                />
              </div>
            );
          }
          return (
            <div key={qid} className="bg-eh-cream/30 rounded-2xl p-5" data-testid={`quiz-q-${qIdx}`}>
              <div className="font-medium mb-3" id={`quiz-q-label-${qid}`}>
                {qIdx + 1}. {q.question || <em className="text-eh-muted">{tp("learn.emptyQuestion")}</em>}
              </div>
              <div className="space-y-2" role="radiogroup" aria-labelledby={`quiz-q-label-${qid}`}>
                {(q.choices || []).map((choice, cIdx) => {
                  const selected = answers[qIdx] === cIdx;
                  const answerWasRight = !!resultByQuestion[q.id]?.is_correct;
                  let cls = "border-eh-border-soft hover:border-eh-ink/40";
                  if (shown) {
                    if (selected) cls = answerWasRight ? "border-emerald-500 bg-emerald-50" : "border-rose-500 bg-rose-50";
                    else cls = "border-eh-border-soft opacity-60";
                  } else if (selected) {
                    cls = "border-eh-ink bg-eh-cream";
                  }
                  return (
                    <button
                      key={cIdx}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => {
                        if (shown || locked) return;
                        const next = [...answers];
                        next[qIdx] = cIdx;
                        setAnswers(next);
                      }}
                      disabled={!!shown || locked}
                      data-testid={QUIZ.quizChoice(qid, cIdx)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition flex items-center gap-3 ${cls}`}
                    >
                      <span
                        className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                          selected ? "bg-eh-ink border-eh-ink" : "border-eh-border"
                        }`}
                      >
                        {selected && <span className="w-2 h-2 rounded-full bg-eh-bg" />}
                      </span>
                      <span className="text-sm">{choice || <em className="text-eh-muted">{tp("learn.emptyChoice")}</em>}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p role="alert" className="mb-4 text-sm text-rose-700" data-testid={QUIZ.quizError}>{error}</p>
      )}

      <div className="flex flex-wrap gap-3 items-center">
        {!shown ? (
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!allAnswered || locked || exhausted}
            data-testid={QUIZ.quizSubmit}
            className="bg-eh-ink text-eh-bg rounded-lg px-6 py-2.5 font-medium hover:bg-eh-ink/90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {t("learn.quizSubmit")}
          </button>
        ) : !exhausted && (
          <button
            type="button"
            onClick={retry}
            disabled={disabled}
            data-testid="quiz-retry"
            className="bg-eh-ink text-eh-bg rounded-lg px-6 py-2.5 font-medium hover:bg-eh-ink/90 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" /> {t("learn.quizRetry")}
          </button>
        )}
        {!allAnswered && !shown && !exhausted && (
          <span className="text-xs text-eh-muted">{t("learn.answerAll")}</span>
        )}
        {left !== null && (
          <span
            className={`text-xs ${exhausted ? "text-rose-700 font-medium" : "text-eh-muted"}`}
            data-testid={QUIZ.quizAttemptsLeft}
          >
            {exhausted
              ? tp("quizView.attemptsExhausted")
              : left === 1 ? tp("quizView.attemptsLeft.one") : tp("quizView.attemptsLeft.many", { n: left })}
          </span>
        )}
      </div>
    </div>
  );
}
